import { AppleOutlined, AndroidOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { designTokens } from '@app/theme';
import { parseApiError } from '@shared/lib/api-error';
import { formatDateTime, formatRelative } from '@shared/lib/format-date';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Empty,
  Form,
  Input,
  Modal,
  Spin,
  Tag,
  Tooltip,
  Typography,
  notification,
} from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AdminDeviceResponse } from '@api/generated/types.gen';

import { confirmDeviceKyc, fetchUserDevices } from '../api';

const { Text } = Typography;

type DevicesBlockProps = {
  userId: string;
  canManage: boolean;
};

/**
 * Блок «Устройства» в карточке пользователя: все устройства, включая отозванные.
 *
 * Вход по OTP доказывает владение SIM-картой, но не личностью, поэтому новое
 * устройство верифицированного аккаунта в карантине (`kycConfirmedAt == null`),
 * пока владелец не пройдёт liveness в MyID. Если liveness недоступен, оператор
 * с правом `users.manage` снимает карантин вручную — кнопка «Подтвердить
 * устройство», причина обязательна и попадает в неизменяемый аудит
 * (`DEVICE / KYC_CONFIRM_OVERRIDE`).
 *
 * `kycConfirmAttempts > 0` — liveness на устройстве проходил не владелец
 * аккаунта: сигнал возможного мошенничества, подсвечен красным.
 */
export function DevicesBlock({ userId, canManage }: DevicesBlockProps) {
  const { t } = useTranslation(['users', 'common', 'errors']);
  const qc = useQueryClient();
  const [form] = Form.useForm<{ reason: string }>();
  const [confirmTarget, setConfirmTarget] = useState<AdminDeviceResponse | null>(null);

  const devicesQuery = useQuery({
    queryKey: ['admin-user-devices', userId],
    queryFn: () => fetchUserDevices(userId),
    enabled: userId !== '',
  });

  const confirmMutation = useMutation({
    mutationFn: async (input: { deviceId: string; reason: string }) => {
      await confirmDeviceKyc(userId, input.deviceId, input.reason);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-user-devices', userId] });
      setConfirmTarget(null);
      form.resetFields();
      notification.success({
        message: t('users:devices.confirmed'),
        placement: 'topRight',
      });
    },
    onError: (err: unknown) => {
      const apiErr = parseApiError(
        (err as { response?: { data?: unknown } }).response?.data,
        (err as { response?: { status?: number } }).response?.status,
      );
      notification.error({
        message: t(`errors:${apiErr.code}`, { defaultValue: t('errors:UNKNOWN_ERROR') }),
        placement: 'topRight',
      });
    },
  });

  const devices = devicesQuery.data ?? [];

  const submitConfirm = async () => {
    let values: { reason: string };
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    confirmMutation.mutate({
      deviceId: confirmTarget?.id ?? '',
      reason: values.reason.trim(),
    });
  };

  return (
    <Card
      title={t('users:devices.title')}
      style={{ borderColor: designTokens.slate200 }}
      styles={{
        header: { borderBottomColor: designTokens.slate200, fontWeight: 600 },
        body: { padding: 0 },
      }}
    >
      <Spin spinning={devicesQuery.isLoading}>
        {devicesQuery.isError ? (
          <div style={{ padding: '16px' }}>
            <Alert type="error" showIcon message={t('users:devices.loadError')} />
          </div>
        ) : !devicesQuery.isLoading && devices.length === 0 ? (
          <div style={{ padding: 32 }}>
            <Empty description={t('users:devices.empty')} />
          </div>
        ) : (
          devices.map((device, idx) => {
            const inQuarantine = device.kycConfirmedAt == null;
            const attempts = device.kycConfirmAttempts ?? 0;
            return (
              <div
                key={device.id ?? idx}
                style={{
                  padding: '12px 16px',
                  borderBlockStart: idx > 0 ? `1px solid ${designTokens.slate100}` : 'none',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                  }}
                >
                  <Text
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: device.revoked ? designTokens.slate400 : designTokens.navy900,
                    }}
                  >
                    {device.platform === 'IOS' ? (
                      <AppleOutlined style={{ marginInlineEnd: 6 }} />
                    ) : (
                      <AndroidOutlined style={{ marginInlineEnd: 6 }} />
                    )}
                    {device.name ?? t('users:devices.unnamed')}
                  </Text>
                  <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {attempts > 0 && (
                      <Tag
                        color="red"
                        style={{ fontSize: 11, fontWeight: 600, marginInlineEnd: 0 }}
                      >
                        {t('users:devices.mismatch', { count: attempts })}
                      </Tag>
                    )}
                    {device.revoked && (
                      <Tag style={{ fontSize: 11, fontWeight: 600, marginInlineEnd: 0 }}>
                        {t('users:devices.revoked')}
                      </Tag>
                    )}
                    {inQuarantine ? (
                      <Tag
                        color="orange"
                        style={{ fontSize: 11, fontWeight: 600, marginInlineEnd: 0 }}
                      >
                        {t('users:devices.quarantine')}
                      </Tag>
                    ) : (
                      <Tooltip title={formatDateTime(device.kycConfirmedAt)}>
                        <Tag
                          color="green"
                          style={{ fontSize: 11, fontWeight: 600, marginInlineEnd: 0 }}
                        >
                          {t('users:devices.kycConfirmed')}
                        </Tag>
                      </Tooltip>
                    )}
                  </span>
                </div>

                <div
                  style={{
                    marginBlockStart: 6,
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '4px 16px',
                  }}
                >
                  <span style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px' }}>
                    <Tooltip
                      title={device.lastUsedAt ? formatDateTime(device.lastUsedAt) : undefined}
                    >
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {t('users:devices.lastUsedAt')}:{' '}
                        {device.lastUsedAt ? formatRelative(device.lastUsedAt) : t('users:never')}
                      </Text>
                    </Tooltip>
                    <Tooltip title={formatDateTime(device.createdAt)}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {t('users:devices.createdAt')}: {formatRelative(device.createdAt)}
                      </Text>
                    </Tooltip>
                  </span>
                  {canManage && inQuarantine && (
                    <Button
                      size="small"
                      icon={<SafetyCertificateOutlined />}
                      onClick={() => {
                        setConfirmTarget(device);
                      }}
                    >
                      {t('users:devices.confirmAction')}
                    </Button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </Spin>

      <Modal
        title={t('users:devices.confirmTitle', {
          name: confirmTarget?.name ?? t('users:devices.unnamed'),
        })}
        open={confirmTarget !== null}
        okText={t('common:actions.confirm')}
        cancelText={t('common:actions.cancel')}
        okButtonProps={{ loading: confirmMutation.isPending }}
        onOk={() => {
          void submitConfirm();
        }}
        onCancel={() => {
          setConfirmTarget(null);
          form.resetFields();
        }}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" requiredMark={false}>
          <Alert
            type="warning"
            showIcon
            style={{ marginBlockEnd: 12 }}
            message={t('users:devices.confirmWarning')}
          />
          {confirmTarget?.revoked && (
            <Alert
              type="info"
              showIcon
              style={{ marginBlockEnd: 12 }}
              message={t('users:devices.confirmRevokedNote')}
            />
          )}
          <Form.Item
            name="reason"
            label={t('users:devices.reasonLabel')}
            rules={[
              {
                required: true,
                whitespace: true,
                message: t('users:devices.reasonRequired'),
              },
            ]}
          >
            <Input.TextArea
              rows={3}
              placeholder={t('users:devices.reasonPlaceholder')}
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
